import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Badge, Button, Alert, Spinner, Row, Col, Label, Input, Table, FormGroup } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  hmrcFilingSettingsDataApi,
  hmrcFilingSettingsSaveApi,
  hmrcFilingVatObligationsApi,
  hmrcFilingVatCalculateApi,
  hmrcFilingVatPreviewApi,
  hmrcFilingVatSubmitApi,
  hmrcFilingVatReturnApi,
  hmrcFilingVatLiabilitiesApi,
  hmrcFilingVatPaymentsApi,
  hmrcFilingSubmissionListApi,
  hmrcFilingSubmissionDataApi,
} from "../../../../helpers/fakebackend_helper";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import {
  clientInfo,
  fmtDate,
  fmtMoney,
  differs,
  EnvBadge,
  StatusBadge,
  OvertypeTable,
  CodeBlock,
  prettyJson,
} from "./HmrcFilingShared";

// Change #100747 - Making Tax Digital VAT returns.
//
// CRMit works out the nine boxes from its own invoices and purchases. The accountant can
// overtype any box (with a reason) because they may have made adjustments outside the CRM.
// The return is previewed as the exact JSON HMRC will receive, then sent to whichever HMRC
// environment the company is connected to (Test = sandbox). Every attempt is kept with what
// CRMit calculated, what was sent and what HMRC said back.

const DERIVED = ["totalVatDue", "netVatDue"];

const derive = (boxes) => {
  const n = (k) => parseFloat(boxes[k]) || 0;
  const totalVatDue = Math.round((n("vatDueSales") + n("vatDueAcquisitions")) * 100) / 100;
  const netVatDue = Math.round(Math.abs(totalVatDue - n("vatReclaimedCurrPeriod")) * 100) / 100;
  return { ...boxes, totalVatDue, netVatDue };
};

export default function HmrcVatReturns() {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [vrn, setVrn] = useState("");
  const [saving, setSaving] = useState(false);

  const [obligations, setObligations] = useState(null);
  const [loadingObligations, setLoadingObligations] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const [prepare, setPrepare] = useState(null); // { obligation, initial?, resubmits? }
  const [viewing, setViewing] = useState(null);
  const [hmrcReturn, setHmrcReturn] = useState(null);
  const [account, setAccount] = useState(null); // { kind, rows, from, to }
  const [busy, setBusy] = useState("");

  const loadMeta = useCallback(async () => {
    try {
      const response = await hmrcFilingSettingsDataApi();
      if (response?.status === 200) {
        setMeta(response);
        setVrn(response.settings?.vat_vrn || "");
      } else if (response?.message) {
        toast.error(response.error || response.message);
      }
    } catch (e) {
      toast.error("Could not load the HMRC filing details.");
    }
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const response = await hmrcFilingSubmissionListApi("VAT");
      if (response?.status === 200) setSubmissions(response.hmrcsubmission_list || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const loadObligations = useCallback(async () => {
    setLoadingObligations(true);
    try {
      const response = await hmrcFilingVatObligationsApi([{ ClientInfo: clientInfo() }]);
      if (response?.status === 200) setObligations(response.obligations || []);
      else toast.error(response?.error || "Could not get the VAT obligations from HMRC.");
    } catch (e) {
      toast.error("Could not get the VAT obligations from HMRC.");
    }
    setLoadingObligations(false);
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([loadMeta(), loadSubmissions()]);
      setLoading(false);
    })();
  }, [loadMeta, loadSubmissions]);

  const saveVrn = async () => {
    setSaving(true);
    try {
      const response = await hmrcFilingSettingsSaveApi([{ Vrn: vrn }]);
      if (response?.status === 200) {
        toast.success("VAT registration number saved");
        loadMeta();
      } else toast.error(response?.error || "Could not save.");
    } catch (e) {
      toast.error("Could not save.");
    }
    setSaving(false);
  };

  const openSubmission = async (id) => {
    setBusy(`view-${id}`);
    try {
      const response = await hmrcFilingSubmissionDataApi(id);
      if (response?.status === 200) setViewing(response.hmrcsubmission_data);
      else toast.error(response?.error || "Could not load the submission.");
    } catch (e) {
      toast.error("Could not load the submission.");
    }
    setBusy("");
  };

  // Reopen the return pre-filled with what was sent last time.
  const resubmit = async (id) => {
    setBusy(`resubmit-${id}`);
    try {
      const response = await hmrcFilingSubmissionDataApi(id);
      const s = response?.hmrcsubmission_data;
      if (response?.status === 200 && s) {
        const reasons = {};
        (s.Overtypes || []).forEach((o) => {
          reasons[o.Field] = o.Reason || "";
        });
        setViewing(null);
        setPrepare({
          obligation: { periodKey: s.PeriodKey, start: s.PeriodStartDate, end: s.PeriodEndDate },
          initial: s.Submitted,
          reasons,
          vatPeriodId: s.VatPeriod_Id,
          resubmits: s.Id,
        });
      } else toast.error(response?.error || "Could not load the submission.");
    } catch (e) {
      toast.error("Could not load the submission.");
    }
    setBusy("");
  };

  const viewAtHmrc = async (periodKey) => {
    setBusy(`hmrc-${periodKey}`);
    try {
      const response = await hmrcFilingVatReturnApi([{ PeriodKey: periodKey, ClientInfo: clientInfo() }]);
      if (response?.status === 200) setHmrcReturn({ ...response.vatreturn, labels: response.box_labels });
      else toast.error(response?.error || "HMRC did not return that period.");
    } catch (e) {
      toast.error("HMRC did not return that period.");
    }
    setBusy("");
  };

  const loadAccount = async (kind) => {
    setBusy(kind);
    try {
      const api = kind === "payments" ? hmrcFilingVatPaymentsApi : hmrcFilingVatLiabilitiesApi;
      const response = await api([{ ClientInfo: clientInfo() }]);
      if (response?.status === 200) setAccount({ kind, rows: response[kind] || [], from: response.from, to: response.to });
      else toast.error(response?.error || `Could not get ${kind} from HMRC.`);
    } catch (e) {
      toast.error(`Could not get ${kind} from HMRC.`);
    }
    setBusy("");
  };

  if (loading) {
    return (
      <Card className="p-2">
        <LoadingSpinner />
      </Card>
    );
  }

  const environment = meta?.vat_environment === "live" ? "live" : "test";
  const connected = Boolean(meta?.vat_connected);
  const vrnSaved = Boolean(meta?.settings?.vat_vrn);

  return (
    <Card className="p-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h5 className="mb-0">VAT returns (Making Tax Digital)</h5>
        <Badge color={environment === "live" ? "success" : "warning"}>{environment === "live" ? "HMRC Live" : "HMRC Test (Sandbox)"}</Badge>
      </div>
      <p className="text-muted mt-2 mb-0">
        CRMit works out each box from your invoices and purchases. You can overtype any figure before sending, for
        example after your accountant's own checks. Both sets of figures are kept with every submission. Your
        invoices and purchases are never changed.
      </p>
      {!connected && (
        <Alert color="warning" className="mt-3 mb-0 py-2">
          Not connected to HMRC. Connect on the Connection tab first.
        </Alert>
      )}

      <hr />

      <h6 className="mb-2">Filing details</h6>
      <Row className="g-2 align-items-end">
        <Col md={4}>
          <Label className="form-label">VAT registration number</Label>
          <Input value={vrn} onChange={(e) => setVrn(e.target.value)} placeholder="123456789" maxLength={12} />
          {meta?.vat_vrn_source === "company" && (
            <small className="text-muted d-block mt-1">Taken from the VAT number on your Company settings. Save to use a different number here.</small>
          )}
        </Col>
        <Col md="auto">
          <Button color="primary" onClick={saveVrn} disabled={saving}>
            {saving && <Spinner size="sm" className="me-1" />}
            Save
          </Button>
        </Col>
      </Row>

      <hr />

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h6 className="mb-0">Obligations from HMRC</h6>
        <div className="d-flex gap-2 flex-wrap">
          <Button color="primary" outline size="sm" onClick={() => loadAccount("liabilities")} disabled={!connected || !vrnSaved || Boolean(busy)}>
            {busy === "liabilities" && <Spinner size="sm" className="me-1" />}
            Liabilities
          </Button>
          <Button color="primary" outline size="sm" onClick={() => loadAccount("payments")} disabled={!connected || !vrnSaved || Boolean(busy)}>
            {busy === "payments" && <Spinner size="sm" className="me-1" />}
            Payments
          </Button>
          <Button color="primary" size="sm" onClick={loadObligations} disabled={!connected || !vrnSaved || loadingObligations}>
            {loadingObligations ? <Spinner size="sm" className="me-1" /> : <i className="ri-refresh-line me-1" />}
            Get obligations
          </Button>
        </div>
      </div>
      {!vrnSaved && <Alert color="info" className="py-2">Save the VAT registration number above first.</Alert>}

      {obligations === null ? (
        <p className="text-muted small mb-0">Press Get obligations to see which returns HMRC is expecting.</p>
      ) : obligations.length === 0 ? (
        <p className="text-muted mb-0">HMRC has no obligations in the last 15 months.</p>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Period</th>
                <th>Due</th>
                <th>Period key</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {obligations.map((o) => (
                <tr key={o.periodKey}>
                  <td>
                    {fmtDate(o.start)} to {fmtDate(o.end)}
                  </td>
                  <td>
                    {fmtDate(o.due)}
                    {o.status === "O" && moment(o.due).isBefore(moment(), "day") && <Badge color="danger" className="ms-1">Overdue</Badge>}
                  </td>
                  <td className="font-monospace">{o.periodKey}</td>
                  <td>
                    {o.status === "F" ? (
                      <Badge color="success">Fulfilled {o.received ? fmtDate(o.received) : ""}</Badge>
                    ) : (
                      <Badge color="warning">Open</Badge>
                    )}
                  </td>
                  <td className="text-end text-nowrap">
                    {o.status === "F" ? (
                      <Button size="sm" color="light" onClick={() => viewAtHmrc(o.periodKey)} disabled={busy === `hmrc-${o.periodKey}`}>
                        {busy === `hmrc-${o.periodKey}` && <Spinner size="sm" className="me-1" />}
                        View at HMRC
                      </Button>
                    ) : (
                      <Button size="sm" color="primary" onClick={() => setPrepare({ obligation: o })}>
                        <i className="ri-file-edit-line me-1" />
                        Prepare return
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <hr />

      <h6 className="mb-2">Submissions</h6>
      {submissions.length === 0 ? (
        <p className="text-muted mb-0">No VAT returns have been sent from CRMit yet.</p>
      ) : (
        <SubmissionTable rows={submissions} busy={busy} onView={openSubmission} onResubmit={resubmit} />
      )}

      {prepare && (
        <VatReturnModal
          prepare={prepare}
          environment={environment}
          vrn={meta?.settings?.vat_vrn || ""}
          labels={meta?.vat_box_labels || {}}
          editable={meta?.vat_editable_boxes || []}
          onClose={() => setPrepare(null)}
          onSent={() => {
            setPrepare(null);
            loadSubmissions();
            if (obligations !== null) loadObligations();
          }}
        />
      )}

      <AppModal
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing ? `VAT return ${viewing.PeriodKey}` : ""}
        subtitle={viewing ? `${fmtDate(viewing.PeriodStartDate)} to ${fmtDate(viewing.PeriodEndDate)} - sent ${fmtDate(viewing.CreatedDateTime, "DD MMM YYYY HH:mm")}` : ""}
        size="lg"
        hideCancel
        cancelLabel="Close"
      >
        {viewing && (
          <>
            <p className="mb-2">
              <EnvBadge env={viewing.Environment} /> <StatusBadge code={viewing.StatusCode} />
              {viewing.StatusMessage}
            </p>
            {viewing.ReceiptReference && (
              <p className="small mb-2">
                HMRC receipt (form bundle) <span className="font-monospace">{viewing.ReceiptReference}</span>
                {viewing.ChargeRefNumber && (
                  <>
                    {" - "}charge reference <span className="font-monospace">{viewing.ChargeRefNumber}</span>
                  </>
                )}
                {viewing.PaymentIndicator && <> - {viewing.PaymentIndicator === "DD" ? "paid by Direct Debit" : viewing.PaymentIndicator === "BANK" ? "repayment to bank" : viewing.PaymentIndicator}</>}
              </p>
            )}
            <BoxComparison labels={meta?.vat_box_labels || {}} calculated={viewing.Calculated?.boxes} submitted={viewing.Submitted} />
            <h6 className="mt-3">Overtypes</h6>
            <OvertypeTable overtypes={viewing.Overtypes} />
            {viewing.Calculated?.basis && (
              <p className="small text-muted">
                CRMit figures from {viewing.Calculated.basis === "VAT_PERIOD" ? "the CRM VAT period" : "document dates"}: {viewing.Calculated.counts?.invoices ?? 0} invoices,{" "}
                {viewing.Calculated.counts?.purchases ?? 0} purchases.
              </p>
            )}
            <h6 className="mt-3">Sent to HMRC</h6>
            <CodeBlock text={prettyJson(viewing.RequestPayload)} maxHeight={200} />
            <h6 className="mt-3">HMRC response (HTTP {viewing.HttpCode})</h6>
            <CodeBlock text={prettyJson(viewing.ResponseBody)} maxHeight={200} />
            {viewing.StatusCode !== "ACCEPTED" && (
              <div className="text-end mt-3">
                <Button color="primary" outline onClick={() => resubmit(viewing.Id)}>
                  <i className="ri-refresh-line me-1" />
                  Resubmit
                </Button>
              </div>
            )}
          </>
        )}
      </AppModal>

      <AppModal open={Boolean(hmrcReturn)} onClose={() => setHmrcReturn(null)} title={hmrcReturn ? `Return ${hmrcReturn.periodKey} as held by HMRC` : ""} size="md" hideCancel cancelLabel="Close">
        {hmrcReturn && (
          <Table size="sm" className="mb-0">
            <tbody>
              {Object.entries(hmrcReturn.labels || {}).map(([k, label]) => (
                <tr key={k}>
                  <td>{label}</td>
                  <td className="text-end">{fmtMoney(hmrcReturn[k])}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </AppModal>

      <AppModal
        open={Boolean(account)}
        onClose={() => setAccount(null)}
        title={account ? (account.kind === "payments" ? "VAT payments" : "VAT liabilities") : ""}
        subtitle={account ? `${fmtDate(account.from)} to ${fmtDate(account.to)}` : ""}
        size="lg"
        hideCancel
        cancelLabel="Close"
      >
        {account && (account.rows.length === 0 ? <p className="text-muted mb-0">HMRC has nothing in this window.</p> : <AccountTable kind={account.kind} rows={account.rows} />)}
      </AppModal>
    </Card>
  );
}

function SubmissionTable({ rows, busy, onView, onResubmit }) {
  return (
    <div className="table-responsive">
      <Table size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Sent</th>
            <th>Period</th>
            <th>Service</th>
            <th>Receipt</th>
            <th>Overtyped</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.Id}>
              <td>{fmtDate(s.CreatedDateTime, "DD MMM YYYY HH:mm")}</td>
              <td>
                {fmtDate(s.PeriodEndDate)} <span className="font-monospace small text-muted">{s.PeriodKey}</span>
                {s.Resubmits___HmrcSubmission_Id && <div className="small text-muted">Resubmission of #{s.Resubmits___HmrcSubmission_Id}</div>}
              </td>
              <td>
                <EnvBadge env={s.Environment} />
              </td>
              <td className="font-monospace small">{s.ReceiptReference || "-"}</td>
              <td>{s.QtyOvertypes > 0 ? <Badge color="warning">{s.QtyOvertypes}</Badge> : <span className="text-muted small">none</span>}</td>
              <td style={{ maxWidth: 340 }}>
                <StatusBadge code={s.StatusCode} />
                <span className="small text-muted">{s.StatusMessage}</span>
              </td>
              <td className="text-end text-nowrap">
                <Button size="sm" color="light" className="me-1" onClick={() => onView(s.Id)} disabled={busy === `view-${s.Id}`}>
                  View
                </Button>
                {s.StatusCode !== "ACCEPTED" && (
                  <Button size="sm" color="primary" outline onClick={() => onResubmit(s.Id)} disabled={busy === `resubmit-${s.Id}`}>
                    {busy === `resubmit-${s.Id}` ? <Spinner size="sm" className="me-1" /> : <i className="ri-refresh-line me-1" />}
                    Resubmit
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function BoxComparison({ labels, calculated, submitted }) {
  return (
    <Table size="sm" className="align-middle mb-0">
      <thead>
        <tr>
          <th>Box</th>
          <th className="text-end">CRMit calculated</th>
          <th className="text-end">Submitted</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(labels).map(([k, label]) => {
          const changed = calculated && submitted && differs(calculated[k], submitted[k]);
          return (
            <tr key={k} className={changed ? "table-warning" : ""}>
              <td>{label}</td>
              <td className="text-end">{calculated ? fmtMoney(calculated[k]) : "-"}</td>
              <td className="text-end fw-semibold">{submitted ? fmtMoney(submitted[k]) : "-"}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function AccountTable({ kind, rows }) {
  if (kind === "payments") {
    return (
      <Table size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Received</th>
            <th className="text-end">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{fmtDate(r.received)}</td>
              <td className="text-end">{fmtMoney(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
  return (
    <Table size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Type</th>
          <th>Period</th>
          <th>Due</th>
          <th className="text-end">Original</th>
          <th className="text-end">Outstanding</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.type}</td>
            <td>{r.taxPeriod ? `${fmtDate(r.taxPeriod.from)} to ${fmtDate(r.taxPeriod.to)}` : "-"}</td>
            <td>{fmtDate(r.due)}</td>
            <td className="text-end">{fmtMoney(r.originalAmount)}</td>
            <td className="text-end">{fmtMoney(r.outstandingAmount)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function VatReturnModal({ prepare, environment, vrn, labels, editable, onClose, onSent }) {
  const { obligation } = prepare;
  const start = obligation.start;
  const end = obligation.end;

  const [step, setStep] = useState("form"); // form | preview
  const [calc, setCalc] = useState(null);
  const [boxes, setBoxes] = useState(null);
  const [reasons, setReasons] = useState(prepare.reasons || {});
  const [preview, setPreview] = useState(null);
  const [declaration, setDeclaration] = useState(false);
  const [confirmVrn, setConfirmVrn] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await hmrcFilingVatCalculateApi([
          { PeriodStartDate: start, PeriodEndDate: end, ...(prepare.vatPeriodId ? { VatPeriod_Id: prepare.vatPeriodId } : {}) },
        ]);
        if (response?.status === 200) {
          setCalc(response);
          // A resubmission starts from what was sent last time; a new return from CRMit's figures.
          const startFrom = prepare.initial || response.boxes;
          setBoxes(Object.fromEntries(editable.map((k) => [k, String(startFrom?.[k] ?? response.boxes[k] ?? 0)])));
        } else {
          toast.error(response?.error || "Could not work out the VAT figures.");
        }
      } catch (e) {
        toast.error("Could not work out the VAT figures.");
      }
      setBusy(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitted = useMemo(() => (boxes ? derive(boxes) : null), [boxes]);

  const payload = () => ({
    PeriodKey: obligation.periodKey,
    PeriodStartDate: start,
    PeriodEndDate: end,
    ...(calc?.vatperiod?.Id ? { VatPeriod_Id: calc.vatperiod.Id } : {}),
    Boxes: Object.fromEntries(editable.map((k) => [k, boxes[k] === "" ? 0 : boxes[k]])),
    Reasons: reasons,
  });

  const doPreview = async () => {
    setBusy(true);
    try {
      const response = await hmrcFilingVatPreviewApi([payload()]);
      if (response?.status === 200) {
        setPreview(response);
        setStep("preview");
      } else toast.error(response?.error || "Could not build the preview.");
    } catch (e) {
      toast.error("Could not build the preview.");
    }
    setBusy(false);
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      const response = await hmrcFilingVatSubmitApi([
        {
          ...payload(),
          Declaration: true,
          ConfirmVrn: environment === "live" ? confirmVrn : "",
          ClientInfo: clientInfo(),
          ...(prepare.resubmits ? { Resubmits___HmrcSubmission_Id: prepare.resubmits } : {}),
        },
      ]);
      if (response?.status === 200) {
        toast.success(`VAT return accepted by HMRC ${environment === "live" ? "" : "(test) "}- receipt ${response.submission?.ReceiptReference || ""}`);
        onSent();
        return;
      }
      toast.error(response?.error || "HMRC did not accept the return.");
      if (response?.submission) onSent(); // the failed attempt is in the history
    } catch (e) {
      toast.error("Could not reach CRMit to send the return.");
    }
    setBusy(false);
  };

  const overtypedMissingReason = submitted && calc ? editable.filter((k) => differs(calc.boxes[k], submitted[k]) && !(reasons[k] || "").trim()) : [];
  const liveConfirmed = environment !== "live" || confirmVrn.replace(/\D/g, "") === vrn;

  return (
    <AppModal
      open
      onClose={onClose}
      title={`VAT return ${obligation.periodKey}${prepare.resubmits ? " (resubmission)" : ""}`}
      subtitle={`${fmtDate(start)} to ${fmtDate(end)} - VRN ${vrn}`}
      size="lg"
      loading={busy}
      footer={
        step === "form" ? (
          <>
            <Button color="light" onClick={onClose}>
              Cancel
            </Button>
            <Button color="primary" onClick={doPreview} disabled={busy || !boxes}>
              Preview return
            </Button>
          </>
        ) : (
          <>
            <Button color="light" onClick={() => setStep("form")} disabled={busy}>
              Back
            </Button>
            <Button
              color={environment === "live" ? "danger" : "primary"}
              onClick={doSubmit}
              disabled={busy || !declaration || !liveConfirmed || (preview?.errors || []).length > 0 || Boolean(preview?.already_filed)}
            >
              {environment === "live" ? "Submit to HMRC" : "Send to HMRC test service"}
            </Button>
          </>
        )
      }
    >
      {!calc || !boxes ? null : step === "form" ? (
        <>
          <p className="small text-muted">
            Figures from {calc.basis === "VAT_PERIOD" ? `CRM VAT period ${fmtDate(calc.vatperiod?.StartDate)} to ${fmtDate(calc.vatperiod?.EndDate)}` : "invoice and purchase dates"}:{" "}
            {calc.counts?.invoices} invoices, {calc.counts?.purchases} purchases. Overtype any box your accountant has adjusted. Boxes 3 and 5 follow from the others,
            as HMRC requires.
          </p>
          {(calc.warnings || []).map((w, i) => (
            <Alert key={i} color="warning" className="py-2">
              {w}
            </Alert>
          ))}
          <div className="table-responsive">
            <Table size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>Box</th>
                  <th className="text-end" style={{ width: 130 }}>
                    CRMit
                  </th>
                  <th className="text-end" style={{ width: 150 }}>
                    To submit
                  </th>
                  <th style={{ width: 220 }}>Reason for change</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(labels).map(([k, label]) => {
                  const isDerived = DERIVED.includes(k);
                  const changed = differs(calc.boxes[k], submitted[k]);
                  return (
                    <tr key={k} className={changed ? "table-warning" : ""}>
                      <td className="small">{label}</td>
                      <td className="text-end">{fmtMoney(calc.boxes[k])}</td>
                      <td>
                        {isDerived ? (
                          <div className="text-end fw-semibold">{fmtMoney(submitted[k])}</div>
                        ) : (
                          <Input
                            type="number"
                            step={["totalValueSalesExVAT", "totalValuePurchasesExVAT", "totalValueGoodsSuppliedExVAT", "totalAcquisitionsExVAT"].includes(k) ? "1" : "0.01"}
                            className="text-end"
                            value={boxes[k]}
                            onChange={(e) => setBoxes((b) => ({ ...b, [k]: e.target.value }))}
                          />
                        )}
                      </td>
                      <td>
                        {changed && !isDerived && (
                          <Input bsSize="sm" value={reasons[k] || ""} placeholder="e.g. accountant's year-end adjustment" onChange={(e) => setReasons((r) => ({ ...r, [k]: e.target.value }))} />
                        )}
                        {changed && !isDerived && (
                          <Button size="sm" color="link" className="p-0 small" onClick={() => setBoxes((b) => ({ ...b, [k]: String(calc.boxes[k]) }))}>
                            Use CRMit figure
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {overtypedMissingReason.length > 0 && <div className="small text-muted">Tip: add a reason for each overtyped box - it is kept with the submission for audit.</div>}
        </>
      ) : (
        <>
          {preview?.already_filed && (
            <Alert color="danger" className="py-2">
              CRMit has already filed this period (submission #{preview.already_filed}). HMRC does not accept a second return for the same period.
            </Alert>
          )}
          {(preview?.errors || []).map((e, i) => (
            <Alert key={i} color="danger" className="py-2">
              {e}
            </Alert>
          ))}
          <BoxComparison labels={labels} calculated={preview?.calculated} submitted={preview?.submitted} />
          <h6 className="mt-3">Overtypes</h6>
          <OvertypeTable overtypes={preview?.overtypes} />
          <h6 className="mt-3">Exactly what HMRC will receive</h6>
          <CodeBlock text={prettyJson(preview?.payload)} maxHeight={180} />
          <FormGroup check className="mt-3">
            <Input type="checkbox" id="vat-declaration" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
            <Label check for="vat-declaration" className="small">
              When I submit this VAT information I am making a legal declaration that the information is true and complete. A false declaration
              can result in prosecution.
            </Label>
          </FormGroup>
          {environment === "live" ? (
            <Alert color="danger" className="mt-2 mb-0">
              This goes to HMRC live and cannot be withdrawn. Type the VAT registration number <strong>{vrn}</strong> to confirm.
              <Input className="mt-2" value={confirmVrn} onChange={(e) => setConfirmVrn(e.target.value)} placeholder={vrn} />
            </Alert>
          ) : (
            <Alert color="info" className="mt-2 mb-0 py-2">
              Connected to the HMRC test service (sandbox). Nothing is filed for real. Switch to Live on the Connection tab to file.
            </Alert>
          )}
        </>
      )}
    </AppModal>
  );
}
